import React, { useEffect, useRef, useState } from 'react';
import { Icon } from './icons';
import { hueOf } from './AccountMenu';

export type DockItem = {
  key: string; // 'sep' / 'spacer' render as dividers; anything else is a button
  label?: string;
  kind?: 'ws' | 'nav' | 'avatar' | 'add';
  glyph?: string;
  icon?: string;
  active?: boolean;
  presence?: string;
  badge?: number;
  onClick?: (e?: React.MouseEvent) => void;
};

/** "The Deck" primary rail: a labeled dark-glass dock — workspace switcher, a bold
 *  New (+), ⌘K, labeled nav (icon + word), and Settings + avatar pinned at the foot.
 *  Crisp monochrome line icons (no 3D tiles). Vertical = left rail (default);
 *  horizontal = bottom bar (appearance.dock_position). */
export function ChatDock({
  header,
  items,
  orientation = 'vertical',
}: {
  header?: React.ReactNode;
  items: DockItem[];
  orientation?: 'vertical' | 'horizontal';
}) {
  const horizontal = orientation === 'horizontal';

  const renderItem = (it: DockItem) => {
    const common = { onClick: (e: React.MouseEvent) => it.onClick?.(e), 'aria-label': it.label, title: it.label } as const;
    if (it.kind === 'add') {
      return (
        <button type="button" key={it.key} className="fx-dock2-plus" {...common}>
          <Icon name="plus" size={22} />
        </button>
      );
    }
    if (it.key === 'search') {
      return (
        <button type="button" key={it.key} className="fx-dock2-search" {...common}>
          <Icon name="search" size={15} /> <span>⌘K</span>
        </button>
      );
    }
    if (it.kind === 'avatar') {
      return (
        <button
          type="button"
          key={it.key}
          className="fx-dock2-ava"
          style={{ ['--h' as string]: hueOf(it.label || it.key) } as React.CSSProperties}
          {...common}
        >
          <span className="fx-glyph">{it.glyph}</span>
          {it.presence && <span className={`fx-dock2-dot${it.presence === 'away' ? ' is-away' : ''}`} />}
        </button>
      );
    }
    return (
      <button type="button" key={it.key} className="fx-dock2-nav" data-key={it.key} aria-current={it.active ? 'true' : undefined} {...common}>
        <span className="fx-dock2-ic">
          {it.icon && <Icon name={it.icon} size={21} />}
          {!!it.badge && <span className="fx-dock2-badge">{it.badge > 9 ? '9+' : it.badge}</span>}
        </span>
        <span className="fx-dock2-lb">{it.label}</span>
      </button>
    );
  };

  return (
    <nav className={`fx-dock2${horizontal ? ' is-horizontal' : ''}`} aria-label="Primary">
      {header}
      {items.map((it, i) =>
        it.key === 'spacer' ? (
          // eslint-disable-next-line react/no-array-index-key
          <div className="fx-dock2-spacer" key={`spacer-${i}`} />
        ) : it.key.startsWith('sep') ? (
          // eslint-disable-next-line react/no-array-index-key
          <div className="fx-dock2-sep" key={`sep-${i}`} />
        ) : (
          renderItem(it)
        )
      )}
    </nav>
  );
}

export type RollerWorkspace = { id: string; name: string };

/**
 * Circular workspace switcher: hover the workspace icon and scroll — the
 * workspaces slide along a circular arc (rolodex) and snap; switching animates.
 */
export function WorkspaceRoller({
  workspaces,
  activeId,
  onSwitch,
  orientation = 'vertical',
}: {
  workspaces: RollerWorkspace[];
  activeId: string | null;
  onSwitch: (id: string) => void;
  orientation?: 'vertical' | 'horizontal';
}) {
  const horizontal = orientation === 'horizontal';
  const n = workspaces.length;
  const activeIdx = Math.max(0, workspaces.findIndex((w) => w.id === activeId));
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(activeIdx); // continuous index position
  const posRef = useRef(pos);
  posRef.current = pos;
  const snapTimer = useRef<number>(0);
  const closeTimer = useRef<number>(0);

  // Follow external switches (e.g. after creating a workspace).
  useEffect(() => {
    if (!open) setPos(activeIdx);
  }, [activeIdx, open]);

  const mod = (v: number) => ((v % n) + n) % n;

  const snap = () => {
    const target = Math.round(posRef.current);
    setPos(target);
    const ws = workspaces[mod(target)];
    if (ws && ws.id !== activeId) onSwitch(ws.id);
  };

  const onWheel = (e: React.WheelEvent) => {
    if (n < 2) return;
    e.preventDefault();
    const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
    const step = Math.max(-1, Math.min(1, delta / 100)) * 0.55;
    setPos((p) => p + step);
    window.clearTimeout(snapTimer.current);
    snapTimer.current = window.setTimeout(snap, 170);
  };

  const enter = () => {
    window.clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const leave = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      setOpen(false);
      snap();
    }, 240);
  };

  const initial = (s: string) => (s.trim()[0] || '?').toUpperCase();
  if (n === 0) return null;

  // Visible ring: items within ±2.4 of the current position, on a circular arc.
  const ring: { ws: RollerWorkspace; off: number; idx: number }[] = [];
  for (let i = 0; i < n; i += 1) {
    let off = i - pos;
    while (off > n / 2) off -= n;
    while (off < -n / 2) off += n;
    if (Math.abs(off) <= 2.4 || n <= 5) ring.push({ ws: workspaces[i], off, idx: i });
  }

  const centered = workspaces[mod(Math.round(pos))];

  return (
    <div
      className={`fx-roller${open ? ' is-open' : ''}${horizontal ? ' is-horizontal' : ''}`}
      onPointerEnter={enter}
      onPointerLeave={leave}
      onWheel={onWheel}
      title={open ? '' : `${centered?.name} — scroll to switch workspace`}
    >
      {!open ? (
        <button type="button" className="fx-tile is-ws fx-roller-face" aria-label={centered?.name}
          style={{ ['--h' as string]: hueOf(centered?.id || centered?.name || '') } as React.CSSProperties}>
          <span className="fx-glyph">{initial(centered?.name || '?')}</span>
        </button>
      ) : (
        <div className="fx-roller-ring" style={{ height: 36 }}>
          {ring.map(({ ws, off, idx }) => {
            // Fan is always a vertical rolodex (rises above the tile) so it never
            // overlaps neighboring dock icons — works for both left and bottom docks.
            const y = off * 40;
            const x = document.documentElement.classList.contains('fx-no-motion') ? 0 : (1 - Math.cos(Math.min(Math.abs(off) * 0.55, 1.4))) * 26; // circular bulge
            const noMotion = document.documentElement.classList.contains('fx-no-motion');
            const scale = noMotion ? 1 : Math.max(0.62, 1 - Math.abs(off) * 0.16);
            const opacity = Math.max(0.15, 1 - Math.abs(off) * 0.32);
            const isCenter = Math.abs(off) < 0.5;
            return (
              <button
                type="button"
                key={ws.id}
                className={`fx-tile is-ws fx-roller-item${isCenter ? ' is-center' : ''}`}
                style={{
                  ['--h' as string]: hueOf(ws.id || ws.name),
                  transform: `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) scale(${scale.toFixed(3)})`,
                  opacity,
                  zIndex: 10 - Math.round(Math.abs(off)),
                } as React.CSSProperties}
                onClick={() => {
                  setPos(idx);
                  if (ws.id !== activeId) onSwitch(ws.id);
                }}
                aria-label={ws.name}
              >
                <span className="fx-glyph">{initial(ws.name)}</span>
              </button>
            );
          })}
          <span className="fx-roller-name">{centered?.name}</span>
        </div>
      )}
    </div>
  );
}
